import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI, subscriptionAPI } from '../../Components/services/api';
import AdminLayout from '../layout/AdminLayout';
import ActivityLogs from './ActivityLogs';
import ManualPaymentVerification from './ManualPaymentVerification';
import StatsCards from './StatsCards';
import SubscriptionsTable from './SubscriptionsTable';
import AnalyticsCharts from './AnalyticsCharts';
import AdminUsers from './AdminUsers';
import ExtendSubscriptionModal from './Modals/ExtendSubscriptionModal';
import DeleteSubscriptionModal from './Modals/DeleteSubscriptionModal';
import CancelSubscriptionModal from './Modals/CancelSubscriptionModal';
import EditSubscriptionModal from './Modals/EditSubscriptionModal';
import { securityService } from './serviceType';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const ITEMS_PER_PAGE = 10;

const AdminDashboardContent = () => {
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [extendMonths, setExtendMonths] = useState(1);

  const location = useLocation();

  // Get active tab from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    } else {
      setActiveTab('overview');
    }
  }, [location]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const statsResponse = await adminAPI.getDashboard();
      console.log('Dashboard stats:', statsResponse.data);
      setStats(statsResponse.data);
      const subsResponse = await subscriptionAPI.getAll();
      console.log('Subscriptions data:', subsResponse.data);
      setSubscriptions(subsResponse.data.subscriptions || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh after mutations elsewhere (add-subscription modal, payment
  // verification, detail page) — these dispatch window events on success.
  useEffect(() => {
    const handleRefreshSubscriptions = () => {
      fetchDashboardData();
    };
    window.addEventListener('refreshSubscriptions', handleRefreshSubscriptions);
    window.addEventListener('refreshSubscriptionsData', handleRefreshSubscriptions);
    return () => {
      window.removeEventListener('refreshSubscriptions', handleRefreshSubscriptions);
      window.removeEventListener('refreshSubscriptionsData', handleRefreshSubscriptions);
    };
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    fetchDashboardData();
    toast.info('Dashboard data refreshed');
  };

  // Moved this function outside the return statement
  const handleOpenAddSubscription = () => {
    window.dispatchEvent(new Event('openAddSubscriptionModal'));
  };

  const handleExtendSubscription = async () => {
    if (!selectedSubscription || !extendMonths || extendMonths < 1) {
      toast.error('Please select valid months to extend');
      return;
    }
    try {
      setActionLoading(true);
      const subscriptionId = selectedSubscription.subscription_id || selectedSubscription.subscriptionId;
      await subscriptionAPI.extend(subscriptionId, extendMonths);
      toast.success(`Subscription extended by ${extendMonths} month(s) successfully!`);
      setShowExtendModal(false);
      setSelectedSubscription(null);
      setExtendMonths(1);
      fetchDashboardData();
    } catch (error) {
      console.error('Error extending subscription:', error);
      toast.error('Failed to extend subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubscription = async () => {
    if (!selectedSubscription) return;
    try {
      setActionLoading(true);
      const subscriptionId = selectedSubscription.subscription_id || selectedSubscription.subscriptionId;
      await subscriptionAPI.delete(subscriptionId);
      toast.success('Subscription deleted successfully!');
      setShowDeleteModal(false);
      setSelectedSubscription(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Failed to delete subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedSubscription) return;
    try {
      setActionLoading(true);
      const subscriptionId = selectedSubscription.subscription_id || selectedSubscription.subscriptionId;
      await subscriptionAPI.cancel(subscriptionId);
      toast.success('Subscription cancelled successfully!');
      setShowCancelModal(false);
      setSelectedSubscription(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowEditModal(true);
  };

  const openExtendModal = (subscription) => {
    setSelectedSubscription(subscription);
    setExtendMonths(1);
    setShowExtendModal(true);
  };

  const openDeleteModal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowDeleteModal(true);
  };

  const openCancelModal = (subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dateString;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      expired: { color: 'bg-red-100 text-red-800', label: 'Expired' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Pagination Logic
  const totalItems = subscriptions.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
  const paginatedSubscriptions = subscriptions.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Chart Data
  const popularPlansData = useMemo(() => {
    if (subscriptions.length === 0) return null;
    const planCounts = subscriptions.reduce((acc, sub) => {
      // Firewall/VAPT details are free text — group them by service instead
      const plan = securityService(sub)?.label || sub.selected_plan || sub.selectedPlan;
      acc[plan] = (acc[plan] || 0) + 1;
      return acc;
    }, {});
    const labels = Object.keys(planCounts);
    const data = Object.values(planCounts);
    return {
      labels,
      datasets: [
        {
          label: 'Plans',
          data,
          backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
          hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
        },
      ],
    };
  }, [subscriptions]);

  const pieOptions = useMemo(() => ({
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          },
        },
      },
      datalabels: {
        formatter: (value, ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(1) + "%";
          return percentage;
        },
        color: '#fff',
        font: { weight: 'bold', size: 12 },
        anchor: 'end',
        align: 'start'
      }
    },
  }), []);

  const billingData = useMemo(() => {
    if (subscriptions.length === 0) return null;
    const cycleCounts = subscriptions.reduce((acc, sub) => {
      const cycle = sub.billing_cycle || sub.billingCycle;
      acc[cycle] = (acc[cycle] || 0) + 1;
      return acc;
    }, {});
    const labels = Object.keys(cycleCounts);
    const data = Object.values(cycleCounts);
    return {
      labels,
      datasets: [
        {
          label: 'Count',
          data,
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [subscriptions]);

  const barOptions = useMemo(() => ({
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true } },
  }), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {activeTab === 'overview' && (
        <div className="px-4 py-6 sm:px-0">
          {stats && <StatsCards stats={stats} />}
          
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Recent Subscriptions</h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleOpenAddSubscription}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Subscription
                </button>
                <span className="text-sm text-gray-500">
                  Showing {startIndex + 1}-{endIndex} of {totalItems} total
                </span>
                <button
                  onClick={handleRefresh}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>

            <SubscriptionsTable
              subscriptions={paginatedSubscriptions}
              totalItems={totalItems}
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={goToPage}
              onEdit={openEditModal}
              onExtend={openExtendModal}
              onCancel={openCancelModal}
              onDelete={openDeleteModal}
              getStatusBadge={getStatusBadge}
              formatDate={formatDate}
              onAddSubscription={handleOpenAddSubscription}
            />
          </div>
        </div>
      )}

      {activeTab === 'subscriptions' && (
        <div className="px-4 py-6 sm:px-0">
          <SubscriptionsTable
            subscriptions={paginatedSubscriptions}
            totalItems={totalItems}
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={goToPage}
            onEdit={openEditModal}
            onExtend={openExtendModal}
            onCancel={openCancelModal}
            onDelete={openDeleteModal}
            getStatusBadge={getStatusBadge}
            formatDate={formatDate}
            showAllColumns={true}
            onAddSubscription={handleOpenAddSubscription}
          />
        </div>
      )}

      {activeTab === 'users' && (
        <div className="px-4 py-6 sm:px-0">
          <AdminUsers />
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="px-4 py-6 sm:px-0">
          <AnalyticsCharts
            popularPlansData={popularPlansData}
            billingData={billingData}
            pieOptions={pieOptions}
            barOptions={barOptions}
          />
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="px-4 py-6 sm:px-0">
          <ActivityLogs />
        </div>
      )}

      {activeTab === 'verify' && (
        <div className="px-4 py-6 sm:px-0">
          <ManualPaymentVerification />
        </div>
      )}

      {/* Modals */}
      {showExtendModal && selectedSubscription && (
        <ExtendSubscriptionModal
          subscription={selectedSubscription}
          extendMonths={extendMonths}
          actionLoading={actionLoading}
          onMonthsChange={setExtendMonths}
          onExtend={handleExtendSubscription}
          onClose={() => setShowExtendModal(false)}
        />
      )}

      {showDeleteModal && selectedSubscription && (
        <DeleteSubscriptionModal
          subscription={selectedSubscription}
          actionLoading={actionLoading}
          onDelete={handleDeleteSubscription}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {showCancelModal && selectedSubscription && (
        <CancelSubscriptionModal
          subscription={selectedSubscription}
          actionLoading={actionLoading}
          onCancel={handleCancelSubscription}
          onClose={() => setShowCancelModal(false)}
        />
      )}

      {showEditModal && selectedSubscription && (
        <EditSubscriptionModal
          subscription={selectedSubscription}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
};

const AdminDashboard = () => {
  return (
    <AdminLayout activeTab="overview">
      <AdminDashboardContent />
    </AdminLayout>
  );
};

export default AdminDashboard;